import { SizeLocales } from "@lib/constants/filtersConstants";
import { OrderValidationFormat } from "@lib/validators/regex";
import type { SiteConfigEntry } from "./types";
import { Market } from "./types";

const loyaltyEnabledForEnv = process.env.NEXT_PUBLIC_LOYALTY_ENABLED === "true";
const isKlarnaSiteMessagingEnabled = process.env.NEXT_PUBLIC_KLARNA_WIDGETS_ENABLED === "true";
const isOmnibusMarket = process.env.NEXT_PUBLIC_OMNIBUS_MARKET === "true";
const notifySizeAvailabilityEnabled =
    process.env.NEXT_PUBLIC_NOTIFY_SIZE_AVAILABILITY_ENABLED === "true";

export const siteConfig: Record<Market, SiteConfigEntry> = {
    [Market.MY]: {
        country: "MY",
        countryName: "Malaysia",
        language: ["en"],
        currency: "MYR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.US],
        sizeGuideCollections: [
            SizeLocales.US,
            SizeLocales.CA,
            SizeLocales.EU,
            SizeLocales.UK,
            SizeLocales.CM,
        ],
        checkout: {
            countryCode: "+60",
        },
        // Get geo data from https://github.com/mihai-craita/countries_center_box/blob/master/countries.json
        geo: {
            centerLat: 4.210484,
            centerLng: 101.975766,
            swLat: 0.853821,
            swLng: 99.640573,
            neLat: 7.363468,
            neLng: 119.269362,
            // initial zoom level defined by UX team
            initZoomLevel: 6,
        },
        orderNumberFormatRegex: OrderValidationFormat[Market.MY],
        reCaptchaIntegration: {
            contactForm: false,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasGrabPayWidget: true,
        hasNonExpressDeliveryProducts: false,
    },
    [Market.SG]: {
        country: "SG",
        countryName: "Singapore",
        language: ["en"],
        currency: "SGD",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.US],
        sizeGuideCollections: [SizeLocales.US, SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+65",
        },
        geo: {
            centerLat: 1.352083,
            centerLng: 103.819836,
            swLat: 1.166398,
            swLng: 103.605575,
            neLat: 1.470772,
            neLng: 104.085557,
            initZoomLevel: 11,
        },
        orderNumberFormatRegex: OrderValidationFormat[Market.SG],
        reCaptchaIntegration: {
            contactForm: false,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: true,
        hasGrabPayWidget: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
    },
    [Market.ES]: {
        country: "ES",
        countryName: "Spain",
        language: ["es"],
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+34",
        },
        geo: {
            centerLat: 40.463667,
            centerLng: -3.74922,
            swLat: 27.637789,
            swLng: -18.160788,
            neLat: 43.79238,
            neLng: 4.327784,
            initZoomLevel: 6,
        },
        orderNumberFormatRegex: OrderValidationFormat[Market.ES],
        reCaptchaIntegration: {
            contactForm: false,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        hasTryAndBuy: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        isOrderHistoryRoutesAllowed: true,
        isOmnibusMarket: isOmnibusMarket,
        trustpilotIntegration: true,
        hasExtendedNewsletterForm: true,
        HasPreviousSalesPrice: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
    },
    [Market.EE]: {
        country: "EE",
        countryName: "Estonian",
        language: ["et"],
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+372",
        },
        geo: {
            centerLat: 58.595272,
            centerLng: 25.013607,
            swLat: 57.509316,
            swLng: 21.764313,
            neLat: 59.685685,
            neLng: 28.210139,
            initZoomLevel: 8,
        },
        orderNumberFormatRegex: OrderValidationFormat[Market.EE],
        reCaptchaIntegration: {
            contactForm: false,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        hasTryAndBuy: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        isOrderHistoryRoutesAllowed: true,
        isOmnibusMarket: isOmnibusMarket,
        trustpilotIntegration: true,
        hasExtendedNewsletterForm: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        HasPreviousSalesPrice: true,
        hasNonExpressDeliveryProducts: false,
    },
    [Market.IN]: {
        country: "IN",
        countryName: "India",
        language: ["en"],
        currency: "INR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.UK, SizeLocales.EU],
        sizeGuideCollections: [SizeLocales.US, SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+91",
        },
        geo: {
            centerLat: 20.593684,
            centerLng: 78.96288,
            swLat: 6.753516,
            swLng: 68.162386,
            neLat: 35.504475,
            neLng: 97.395555,
            initZoomLevel: 6,
        },
        orderNumberFormatRegex: OrderValidationFormat[Market.IN],
        isProductInfoRequired: true,
        showPriceLegalText: true,
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
    },
    [Market.KR]: {
        country: "KR",
        countryName: "South Korea",
        language: ["ko"],
        currency: "KRW",
        shoeSizingConventions: [SizeLocales.KR, SizeLocales.EU],
        sizeGuideCollections: [SizeLocales.KR, SizeLocales.EU, SizeLocales.US, SizeLocales.CM],
        fractionDigits: 0,
        checkout: {
            countryCode: "+82",
        },
        geo: {
            centerLat: 35.907757,
            centerLng: 127.766922,
            swLat: 33.10611,
            swLng: 124.608139,
            neLat: 38.616931,
            neLng: 130.923218,
            initZoomLevel: 8,
        },
        orderNumberFormatRegex: OrderValidationFormat[Market.KR],
        isProductInfoRequired: true,
        reCaptchaIntegration: {
            contactForm: false,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
    },
    [Market.CA]: {
        country: "CA",
        countryName: "Canada",
        language: ["en", "fr"],
        currency: "CAD",
        shoeSizingConventions: [SizeLocales.CA, SizeLocales.EU],
        sizeGuideCollections: [SizeLocales.CA, SizeLocales.US, SizeLocales.EU, SizeLocales.CM],
        fractionDigits: 2,
        checkout: {
            countryCode: "+1",
            requiresSalesTaxEstimation: true,
            hasShippingFees: true,
        },
        geo: {
            centerLat: 56.130366,
            centerLng: -106.346771,
            swLat: 41.676556,
            swLng: -141.00187,
            neLat: 83.115061,
            neLng: -52.619409,
            initZoomLevel: 4,
        },
        orderNumberFormatRegex: OrderValidationFormat[Market.CA],
        reCaptchaIntegration: {
            contactForm: false,
            checkout: false,
            signupForm: false,
        },
        liveChat: true,
        isCountryChangeForBillingAddressAllowed: false,
        isOrderHistoryRoutesAllowed: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        HasPreviousSalesPrice: true,
        externalScripts: {
            attentive: true,
        },
        hasNonExpressDeliveryProducts: false,
    },
    [Market.US]: {
        country: "US",
        countryName: "United States",
        language: ["en"],
        currency: "USD",
        shoeSizingConventions: [SizeLocales.US, SizeLocales.EU],
        sizeGuideCollections: [SizeLocales.US, SizeLocales.CA, SizeLocales.EU, SizeLocales.CM],
        fractionDigits: 2,
        checkout: {
            countryCode: "+1",
            requiresSalesTaxEstimation: true,
            hasShippingFees: true,
            hasShippingDetails: true,
        },
        geo: {
            centerLat: 39.50024,
            centerLng: -98.350891,
            swLat: 25.820898,
            swLng: -124.712891,
            neLat: 49.38024,
            neLng: -66.940662,
            initZoomLevel: 5,
            imperialMetricSystem: true,
        },
        orderNumberFormatRegex: OrderValidationFormat[Market.US],
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        liveChat: true,
        isCountryChangeForBillingAddressAllowed: false,
        isOrderHistoryRoutesAllowed: true,
        supportsGlobalPrivacyControl: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        HasPreviousSalesPrice: true,
        hasExpressCheckout: true,
        hasNonExpressDeliveryProducts: false,
    },
    [Market.AU]: {
        country: "AU",
        countryName: "Australia",
        language: ["en"],
        currency: "AUD",
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.US],
        sizeGuideCollections: [SizeLocales.AU, SizeLocales.EU, SizeLocales.CN, SizeLocales.CM],
        fractionDigits: 0,
        checkout: {
            countryCode: "+61",
            requiresSalesTaxEstimation: true,
        },
        geo: {
            centerLat: -25.274398,
            centerLng: 133.775136,
            swLat: -54.777219,
            swLng: 112.921454,
            neLat: -9.221084,
            neLng: 159.255528,
            initZoomLevel: 5,
            imperialMetricSystem: true,
        },
        liveChat: true,
        orderNumberFormatRegex: OrderValidationFormat[Market.AU],
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        isOrderHistoryRoutesAllowed: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
    },
    [Market.JP]: {
        country: "JP",
        countryName: "Japan",
        language: ["ja"],
        currency: "JPY",
        shoeSizingConventions: [SizeLocales.JP, SizeLocales.EU],
        sizeGuideCollections: [SizeLocales.JP, SizeLocales.EU, SizeLocales.US, SizeLocales.CM],
        fractionDigits: 3,
        checkout: {
            countryCode: "+81",
            requiresSalesTaxEstimation: true,
            minAddressLookupLength: 7,
        },
        geo: {
            centerLat: 36.204824,
            centerLng: 138.252924,
            swLat: 24.046045,
            swLng: 122.93383,
            neLat: 45.522772,
            neLng: 153.98743,
            initZoomLevel: 5,
        },
        orderNumberFormatRegex: OrderValidationFormat[Market.JP],
        reCaptchaIntegration: {
            contactForm: false,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: true,
        amazonPay: {
            enabled: process.env.NEXT_PUBLIC_AMAZON_PAY_ENABLED === "true",
            bannerEnabled: true,
            checkoutJsUrl: "https://static-fe.payments-amazon.com/checkout.js",
            merchantId: "A2YSIM42GJ1TXQ",
            region: "jp",
        },
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasNonExpressDeliveryProducts: false,
    },
    [Market.GB]: {
        country: "GB",
        countryName: "United Kingdom",
        language: ["en"],
        currency: "GBP",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.UK, SizeLocales.EU],
        sizeGuideCollections: [SizeLocales.UK, SizeLocales.EU, SizeLocales.CM],
        checkout: {
            countryCode: "+44",
        },
        geo: {
            centerLat: 55.378051,
            centerLng: -3.435973,
            swLat: 49.562515,
            swLng: -12.649357,
            neLat: 59.86067,
            neLng: 3.976555,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: false,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        isOrderHistoryRoutesAllowed: true,
        isOmnibusMarket: isOmnibusMarket,
        isGiftWrappingAllowed: false,
        hasTryAndBuy: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        onKlarnaSiteMessaging: isKlarnaSiteMessagingEnabled,
        trustpilotIntegration: true,
        hasExtendedNewsletterForm: true,
        hasRaffle: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        HasPreviousSalesPrice: true,
        hasNonExpressDeliveryProducts: true,
    },
    [Market.PL]: {
        country: "PL",
        countryName: "Poland",
        language: ["pl"],
        currency: "PLN",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+48",
        },
        geo: {
            centerLat: 51.919438,
            centerLng: 19.145136,
            swLat: 49.002025,
            swLng: 14.122864,
            neLat: 54.835812,
            neLng: 24.145893,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: false,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        hasTryAndBuy: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        onKlarnaSiteMessaging: isKlarnaSiteMessagingEnabled,
        isOmnibusMarket: isOmnibusMarket,
        trustpilotIntegration: true,
        hasExtendedNewsletterForm: true,
        hasTrueFit: true,
        showParcelLockerId: true,
        hideParcelLockerWorkingHours: true,
        hideAvailabilityInStore: true,
        hasRaffle: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        HasPreviousSalesPrice: true,
        hasExpressCheckout: true,
        hasNonExpressDeliveryProducts: false,
    },
    [Market.PT]: {
        country: "PT",
        countryName: "Portugal",
        language: ["pt"],
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+351",
        },
        geo: {
            centerLat: 39.399872,
            centerLng: -8.224454,
            swLat: 32.40374,
            swLng: -31.27533,
            neLat: 42.154205,
            neLng: -6.190209,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        hasTryAndBuy: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        onKlarnaSiteMessaging: isKlarnaSiteMessagingEnabled,
        isOmnibusMarket: isOmnibusMarket,
        trustpilotIntegration: true,
        hasExtendedNewsletterForm: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        HasPreviousSalesPrice: true,
        hasNonExpressDeliveryProducts: false,
    },
    [Market.HU]: {
        country: "HU",
        countryName: "Hungary",
        language: ["hu"],
        currency: "HUF",
        fractionDigits: 0,
        roundingUnit: 5,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+36",
        },
        geo: {
            centerLat: 47.162494,
            centerLng: 19.503304,
            swLat: 45.737089,
            swLng: 16.113387,
            neLat: 48.585234,
            neLng: 22.898122,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        hasTryAndBuy: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        isOmnibusMarket: isOmnibusMarket,
        trustpilotIntegration: true,
        hasExtendedNewsletterForm: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        HasPreviousSalesPrice: true,
        hasNonExpressDeliveryProducts: false,
    },
    [Market.CZ]: {
        country: "CZ",
        countryName: "Czechia",
        language: ["cs"],
        currency: "CZK",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+420",
        },
        geo: {
            centerLat: 49.817492,
            centerLng: 15.472962,
            swLat: 48.551808,
            swLng: 12.090589,
            neLat: 51.055719,
            neLng: 18.859236,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        hasTryAndBuy: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        onKlarnaSiteMessaging: isKlarnaSiteMessagingEnabled,
        isOmnibusMarket: isOmnibusMarket,
        trustpilotIntegration: true,
        hasExtendedNewsletterForm: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        HasPreviousSalesPrice: true,
        hasNonExpressDeliveryProducts: false,
    },
    [Market.HR]: {
        country: "HR",
        countryName: "Croatia",
        language: ["hr"],
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+385",
        },
        geo: {
            centerLat: 45.1,
            centerLng: 15.2,
            swLat: 42.392265,
            swLng: 13.489691,
            neLat: 46.555223,
            neLng: 19.448052,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        hasTryAndBuy: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        isOmnibusMarket: isOmnibusMarket,
        trustpilotIntegration: true,
        hasExtendedNewsletterForm: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        HasPreviousSalesPrice: true,
        hasNonExpressDeliveryProducts: false,
    },
    [Market.BE]: {
        country: "BE",
        countryName: "Belgium",
        language: ["nl", "fr"],
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+32",
        },
        geo: {
            centerLat: 50.503887,
            centerLng: 4.469936,
            swLat: 49.497013,
            swLng: 2.544795,
            neLat: 51.505145,
            neLng: 6.408124,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: false,
            checkout: false,
            signupForm: false,
        },
        isCountryChangeForBillingAddressAllowed: false,
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        onKlarnaSiteMessaging: isKlarnaSiteMessagingEnabled,
        isOmnibusMarket: isOmnibusMarket,
        trustpilotIntegration: true,
        hasExtendedNewsletterForm: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        HasPreviousSalesPrice: true,
        hasNonExpressDeliveryProducts: false,
    },
    [Market.SI]: {
        country: "SI",
        countryName: "Slovenia",
        language: ["sl"],
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+386",
        },
        geo: {
            centerLat: 46.151241,
            centerLng: 14.995463,
            swLat: 45.421674,
            swLng: 13.375336,
            neLat: 46.876659,
            neLng: 16.596686,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        hasTryAndBuy: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        isOmnibusMarket: isOmnibusMarket,
        trustpilotIntegration: true,
        hasExtendedNewsletterForm: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        HasPreviousSalesPrice: true,
        hasNonExpressDeliveryProducts: false,
    },
    [Market.SK]: {
        country: "SK",
        countryName: "Slovakia",
        language: ["sk"],
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+421",
        },
        geo: {
            centerLat: 48.669026,
            centerLng: 19.699024,
            swLat: 47.731159,
            swLng: 16.833182,
            neLat: 49.613805,
            neLng: 22.558934,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        hasTryAndBuy: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        isOmnibusMarket: isOmnibusMarket,
        trustpilotIntegration: true,
        hasExtendedNewsletterForm: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        HasPreviousSalesPrice: true,
        hasNonExpressDeliveryProducts: false,
    },
    [Market.FR]: {
        country: "FR",
        countryName: "France",
        language: ["fr"],
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+33",
        },
        geo: {
            centerLat: 46.227638,
            centerLng: 2.213749,
            swLat: 41.342328,
            swLng: -5.141228,
            neLat: 51.089166,
            neLng: 9.560068,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        onKlarnaSiteMessaging: isKlarnaSiteMessagingEnabled,
        isOmnibusMarket: isOmnibusMarket,
        trustpilotIntegration: true,
        hasExtendedNewsletterForm: true,
        HasPreviousSalesPrice: true,
        hasNonExpressDeliveryProducts: false,
    },
    [Market.AT]: {
        country: "AT",
        countryName: "Austria",
        language: ["de"],
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+43",
            parcelShops: {
                searchMethod: "address",
            },
        },
        geo: {
            centerLat: 47.516231,
            centerLng: 14.550072,
            swLat: 46.372336,
            swLng: 9.530783,
            neLat: 49.020608,
            neLng: 17.160686,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        onKlarnaSiteMessaging: isKlarnaSiteMessagingEnabled,
        isOmnibusMarket: isOmnibusMarket,
        trustpilotIntegration: true,
        hasExtendedNewsletterForm: true,
        hasTryAndBuy: false,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        HasPreviousSalesPrice: true,
        hasNonExpressDeliveryProducts: false,
    },
    [Market.CH]: {
        country: "CH",
        countryName: "Switzerland",
        language: ["de", "fr"],
        currency: "CHF",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+41",
        },
        geo: {
            centerLat: 46.818188,
            centerLng: 8.227512,
            swLat: 45.81792,
            swLng: 5.95608,
            neLat: 47.808455,
            neLng: 10.49234,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: false,
            checkout: false,
            signupForm: false,
        },
        isCountryChangeForBillingAddressAllowed: false,
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        isOmnibusMarket: isOmnibusMarket,
        trustpilotIntegration: true,
        hasExtendedNewsletterForm: true,
        hasTryAndBuy: false,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        HasPreviousSalesPrice: true,
        hasNonExpressDeliveryProducts: false,
    },
    [Market.DE]: {
        country: "DE",
        countryName: "Germany",
        language: ["de"],
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+49",
            hasDHLExtraFields: true,
        },
        geo: {
            centerLat: 51.165691,
            centerLng: 10.451526,
            swLat: 47.270112,
            swLng: 5.866342,
            neLat: 55.058347,
            neLng: 15.041896,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        isOmnibusMarket: isOmnibusMarket,
        trustpilotIntegration: true,
        hasExtendedNewsletterForm: true,
        hasTrueFit: true,
        hasTryAndBuy: false,
        hasRaffle: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        HasPreviousSalesPrice: true,
        hasNonExpressDeliveryProducts: false,
    },
    [Market.NL]: {
        country: "NL",
        countryName: "Netherlands",
        language: ["nl"],
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+31",
        },
        geo: {
            centerLat: 52.132633,
            centerLng: 5.291266,
            swLat: 50.750384,
            swLng: 3.357962,
            neLat: 53.556021,
            neLng: 7.22751,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        isOmnibusMarket: isOmnibusMarket,
        trustpilotIntegration: true,
        hasExtendedNewsletterForm: true,
        hasTryAndBuy: false,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        HasPreviousSalesPrice: true,
        hasNonExpressDeliveryProducts: false,
    },
    [Market.DK]: {
        country: "DK",
        countryName: "Denmark",
        language: ["da"],
        currency: "DKK",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+45",
        },
        geo: {
            centerLat: 56.1554671,
            centerLng: 10.4309955,
            swLat: 54.464597,
            swLng: 8.072241,
            neLat: 57.751813,
            neLng: 15.197281,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        isOmnibusMarket: isOmnibusMarket,
        trustpilotIntegration: true,
        hasExtendedNewsletterForm: true,
        hasRaffle: true,
        hasTryAndBuy: false,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        HasPreviousSalesPrice: true,
        hasNonExpressDeliveryProducts: false,
    },
    [Market.FI]: {
        country: "FI",
        countryName: "Finland",
        language: ["fi"],
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+358",
        },
        geo: {
            centerLat: 61.92411,
            centerLng: 25.748151,
            swLat: 59.70544,
            swLng: 20.547411,
            neLat: 70.092293,
            neLng: 31.5871,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        isOmnibusMarket: isOmnibusMarket,
        trustpilotIntegration: true,
        hasExtendedNewsletterForm: true,
        hasTryAndBuy: false,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasRaffle: true,
        HasPreviousSalesPrice: true,
        hasNonExpressDeliveryProducts: false,
    },
    [Market.IE]: {
        country: "IE",
        countryName: "Ireland",
        language: ["en"],
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.UK, SizeLocales.EU],
        sizeGuideCollections: [SizeLocales.UK, SizeLocales.EU],
        checkout: {
            countryCode: "+353",
        },
        geo: {
            centerLat: 53.41291,
            centerLng: -8.24389,
            swLat: 51.421938,
            swLng: -10.66958,
            neLat: 55.38849,
            neLng: -5.9947,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        isOmnibusMarket: isOmnibusMarket,
        trustpilotIntegration: true,
        hasExtendedNewsletterForm: true,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        HasPreviousSalesPrice: true,
        hasNonExpressDeliveryProducts: false,
    },
    [Market.LT]: {
        country: "LT",
        countryName: "Lithuania",
        language: ["lt"],
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+370",
        },
        geo: {
            centerLat: 55.169438,
            centerLng: 23.881275,
            swLat: 53.896879,
            swLng: 20.954368,
            neLat: 56.450321,
            neLng: 26.835591,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        isOmnibusMarket: isOmnibusMarket,
        trustpilotIntegration: true,
        hasExtendedNewsletterForm: true,
        hasTryAndBuy: false,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        HasPreviousSalesPrice: true,
        hasNonExpressDeliveryProducts: false,
    },
    [Market.LV]: {
        country: "LV",
        countryName: "Latvia",
        language: ["lv"],
        currency: "EUR",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+371",
        },
        geo: {
            centerLat: 56.879635,
            centerLng: 24.603189,
            swLat: 55.674777,
            swLng: 20.962343,
            neLat: 58.085569,
            neLng: 28.241403,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        isOmnibusMarket: isOmnibusMarket,
        trustpilotIntegration: true,
        hasExtendedNewsletterForm: true,
        hasTryAndBuy: false,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        HasPreviousSalesPrice: true,
        hasNonExpressDeliveryProducts: false,
    },
    [Market.NO]: {
        country: "NO",
        countryName: "Norway",
        language: ["nb"],
        currency: "NOK",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+47",
        },
        geo: {
            centerLat: 60.472024,
            centerLng: 8.468946,
            swLat: 57.959744,
            swLng: 4.500096,
            neLat: 71.185476,
            neLng: 31.168268,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        isOmnibusMarket: isOmnibusMarket,
        trustpilotIntegration: true,
        hasExtendedNewsletterForm: true,
        hasTryAndBuy: false,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasRaffle: true,
        HasPreviousSalesPrice: true,
        hasNonExpressDeliveryProducts: false,
    },
    [Market.SE]: {
        country: "SE",
        countryName: "Sweden",
        language: ["sv"],
        currency: "SEK",
        fractionDigits: 2,
        shoeSizingConventions: [SizeLocales.EU, SizeLocales.UK],
        sizeGuideCollections: [SizeLocales.EU, SizeLocales.UK, SizeLocales.CM],
        checkout: {
            countryCode: "+46",
        },
        geo: {
            centerLat: 60.128161,
            centerLng: 18.643501,
            swLat: 55.32872,
            swLng: 10.963187,
            neLat: 69.059971,
            neLng: 24.166809,
            initZoomLevel: 6,
        },
        reCaptchaIntegration: {
            contactForm: true,
            checkout: true,
            signupForm: true,
        },
        isCountryChangeForBillingAddressAllowed: false,
        isOrderHistoryRoutesAllowed: true,
        isGiftWrappingAllowed: false,
        isMarketWithLoyalty: loyaltyEnabledForEnv,
        isOmnibusMarket: isOmnibusMarket,
        hasTrueFit: true,
        trustpilotIntegration: true,
        hasExtendedNewsletterForm: true,
        hasTryAndBuy: false,
        hasNotifySizeAvailability: notifySizeAvailabilityEnabled,
        hasRaffle: true,
        HasPreviousSalesPrice: true,
        hasNonExpressDeliveryProducts: false,
    },
};
